const SCORE = {
  startingWithSubstring: 3,
  emptyPattern: 3,
  substring: 2,
  fuzzyMatch: 1,
  noMatch: 0,
} as const;

// Safe-guard to avoid blocking the main thread and possible unresponsiveness with matching overly long suggestions.
// This is only used to limit fuzzy matching, substring matching is always attempted first.
const MAX_FUZZY_SUGGESTION_LENGTH = 50_000;

/**
 * Finds the position of the first occurrence of a substring in a string.
 * @param substring - the substring to find
 * @param str - the string to search in
 * @param strTrim - an optional offset to add to the resulting position, used to adjust for normalization trimming
 * @returns the start and end position of the substring in the string, or undefined if not found
 */
function findPosOf(
  substring: string,
  str: string,
  strTrim: number = 0,
): [number, number] | undefined {
  const startIndex = str.indexOf(substring);
  if (startIndex >= 0) {
    return [startIndex + strTrim, startIndex + substring.length + strTrim];
  }
  return undefined;
}

/**
 * Normalizes the string by trimming leading and trailing whitespace and lowercasing it.
 * @param str - the string to normalize
 * @returns the normalized string and the amount of trimmed leading whitespace
 */
function normalizeString(str: string) {
  let normalizedStr = str.trimStart();
  const trimStart = str.length - normalizedStr.length;
  normalizedStr = normalizedStr.trimEnd().toLowerCase();
  return [normalizedStr, trimStart] as const;
}

interface MatchResult {
  match: boolean;
  pos?: [number, number];
  score: number;
}

/**
 * Finds a substring match for the given pattern in the given string.
 * @param pattern - the search pattern
 * @param str - the string to search in
 * @returns whether a match was found, the match score and the position of the match if found
 */
function findSubstringMatch(pattern: string, str: string): MatchResult {
  if (pattern.length === 0) {
    // empty pattern should always match and score highest
    return { match: true, pos: undefined, score: SCORE.emptyPattern };
  }
  const [normalizedPattern] = normalizeString(pattern);
  const [normalizedStr, strTrim] = normalizeString(str);

  const pos = findPosOf(normalizedPattern, normalizedStr, strTrim);
  if (pos !== undefined) {
    // score higher when position starts at the beginning of trimmed string
    const score =
      pos[0] === strTrim ? SCORE.startingWithSubstring : SCORE.substring;
    return {
      match: true,
      pos,
      score,
    };
  }
  return { match: false, score: 0 };
}

/**
 * Backtracks from `rangeEnd` to find the tightest (rightmost) start position
 * for the pattern within the suggestion.
 *
 * @param suggestionText - the suggestion string (already normalized)
 * @param patternText - the pattern string (already normalized)
 * @param rangeEnd - the end index from the forward scan
 * @returns the tightest start index for the range
 */
function findRangeStart(
  suggestionText: string,
  patternText: string,
  rangeEnd: number,
): number {
  let backtrackSuggestionIndex = rangeEnd;
  let backtrackPatternIndex = patternText.length - 1;

  while (backtrackPatternIndex >= 0) {
    // With each iteration, move one character left in the suggestion
    // If there is a match, also move one character left in the pattern
    if (
      suggestionText[backtrackSuggestionIndex] ===
      patternText[backtrackPatternIndex]
    ) {
      backtrackPatternIndex--;
    }
    backtrackSuggestionIndex--;
  }

  return backtrackSuggestionIndex + 1;
}

/**
 * Performs a linear fuzzy pattern match without a regex.
 * Finds the fuzzy match with the smallest span for a `normalizedPattern` inside `normalizedStr`.
 * All characters are allowed between pattern characters.
 *
 * Algorithm:
 *   1. Forward scan: advance through the string, consuming pattern characters greedily.
 *      When the last pattern character is matched we have a possible range end.
 *   2. Backtrack: Move backwards from the range end, consuming pattern characters in reverse.
 *      This shrinks the range start as far right as possible - the tightest span for
 *      that end position.
 *   3. Record the range if it beats the current best (smaller span).
 *   4. Continue the forward scan from just after the range start to find further ranges.
 *
 * @param normalizedPattern - The search pattern (already normalized: trimmed and lowercased)
 * @param normalizedStr - The string to search in (already normalized: trimmed and lowercased)
 * @returns The start and end position of the best range, or undefined if no range exists
 */
function findFuzzyMatchRange(
  patternText: string,
  suggestionText: string,
): [number, number] | undefined {
  const patternLength = patternText.length;
  const suggestionLength = suggestionText.length;

  // A pattern longer than the suggestion can never match
  if (patternLength > suggestionLength) {
    return undefined;
  }

  // Skip fuzzy matching for overly long suggestions
  if (suggestionLength > MAX_FUZZY_SUGGESTION_LENGTH) {
    return undefined;
  }

  let bestRangeStart = -1;
  let bestRangeEnd = -1;
  let bestRangeLength = Number.POSITIVE_INFINITY;

  let suggestionIndex = 0;
  let patternIndex = 0;

  // Forward scan to find possible match ranges
  while (suggestionIndex < suggestionLength) {
    const remainingSuggestionTextLength = suggestionLength - suggestionIndex;
    const remainingPatternTextLength = patternLength - patternIndex;

    // If the remaining suggestion is shorter than the remaining pattern, no additional match is possible
    if (remainingSuggestionTextLength < remainingPatternTextLength) {
      break;
    }

    // Try to match the current pattern character with the current suggestion character
    // When there is no match, move to the next character in the suggestion
    if (suggestionText[suggestionIndex] !== patternText[patternIndex]) {
      suggestionIndex++;
      continue;
    }

    // When there is a match, move to the next character in both the pattern and the suggestion
    patternIndex++;
    suggestionIndex++;

    // If we have matched the entire pattern, we have a possible range end.
    // The forward scan guarantees that all pattern chars exist within [0, suggestionIndex),
    // now we need to find the tightest possible range for this end position by backtracking.
    if (patternIndex === patternLength) {
      const rangeEnd = suggestionIndex;
      const rangeStart = findRangeStart(suggestionText, patternText, rangeEnd);
      const rangeLength = rangeEnd - rangeStart;

      // Check if this range is better than the one we have found so far
      if (rangeLength < bestRangeLength) {
        bestRangeStart = rangeStart;
        bestRangeEnd = rangeEnd;
        bestRangeLength = rangeLength;
      }

      // Continue searching from the next character after the current range start
      suggestionIndex = rangeStart + 1;
      patternIndex = 0;
    }
  }

  if (bestRangeStart >= 0) {
    return [bestRangeStart, bestRangeEnd];
  }

  return undefined;
}

/**
 * Finds a fuzzy match for the given pattern in the given string.
 * Normalizes the input and assigns scores for the match.
 * This function is only called when no substring match has been found.
 */
function findFuzzyMatch(pattern: string, str: string): MatchResult {
  if (pattern.length === 0) {
    // empty pattern should always match and score highest
    return { match: true, pos: undefined, score: SCORE.emptyPattern };
  }

  // Normalize both sides
  const [normalizedPattern] = normalizeString(pattern);
  const [normalizedStr, strTrim] = normalizeString(str);

  const range = findFuzzyMatchRange(normalizedPattern, normalizedStr);
  if (range !== undefined) {
    // Re-add the leading whitespace offset that was removed by normalization.
    return {
      match: true,
      pos: [range[0] + strTrim, range[1] + strTrim],
      score: SCORE.fuzzyMatch,
    };
  }

  return {
    match: false,
    score: SCORE.noMatch,
  };
}

export function fuzzyMatch(pattern: string, str: string): MatchResult {
  const substringMatch = findSubstringMatch(pattern, str);
  if (substringMatch.match) {
    return substringMatch;
  }

  return findFuzzyMatch(pattern, str);
}

const maxLabelLength = 53; // Maximum amount of characters in the autocomplete label
const middleEllipsisStartPos = 26; // Characters before the middle ellipsis
const middleEllipsisLength = 1; // Length of the middle ellipsis (…)

// Characters after the middle ellipsis
const afterMiddleEllipsisLength =
  maxLabelLength - middleEllipsisStartPos - middleEllipsisLength;

function getMiddleEllipsisEndPos(label: string) {
  return label.length - afterMiddleEllipsisLength;
}

export function labelShouldBeTruncated(label: string): boolean {
  return label.length > maxLabelLength;
}

/* Will add middle ellipsis (…) if the label is longer than maxLabelLength characters */
export function convertToMiddleEllipsis(label: string): string {
  return `${label.slice(0, middleEllipsisStartPos)}…${label.slice(
    getMiddleEllipsisEndPos(label),
  )}`;
}

function convertPosToMiddleEllipsis(
  position: number,
  label: string,
  defaultValue: number,
): number {
  if (position <= middleEllipsisStartPos) {
    return position;
  }

  const endPos = getMiddleEllipsisEndPos(label);

  if (position > endPos) {
    return middleEllipsisStartPos + middleEllipsisLength + (position - endPos);
  }

  return defaultValue;
}

export function getFuzzyMatchFullLabelPosition(
  pattern: string,
  fullLabel: string | undefined,
) {
  if (fullLabel === undefined) {
    return [];
  }
  const isTruncated = labelShouldBeTruncated(fullLabel);

  const pos = fuzzyMatch(pattern, fullLabel).pos;

  if (pos === undefined) {
    return undefined;
  }

  if (!isTruncated) {
    return pos;
  }

  return [
    convertPosToMiddleEllipsis(pos[0], fullLabel, middleEllipsisStartPos),
    convertPosToMiddleEllipsis(
      pos[1],
      fullLabel,
      middleEllipsisStartPos + middleEllipsisLength,
    ),
  ];
}
